import type { AgentBody, AgentSummary, AgentVersion, ResolvedAgent } from '@ralph/contracts'
import { ApiRequestError, api, apiGet } from './client.ts'

/**
 * Agent administration endpoints.
 *
 * One definition per agent (PRD-26 D-23): the per-group layers and their `/scopes/:scopeId`
 * routes are gone, so every call names the agent and nothing else. `scopeId` survives on the
 * payloads the server returns — it is always `'*'` — and is read, never chosen.
 */

/** One stored layer. `body` is a LAYER: absent fields mean "inherit", not "empty". */
export type AgentLayer = {
  scopeId: string
  body: AgentBody
  version: number
  enabled: boolean
  displayName: string
  description: string
}

export type AgentDetail = { agentId: string; layers: AgentLayer[] }

export type AgentResolution = {
  agent: ResolvedAgent
  /** Always empty now that there are no group layers to drop; kept so the response shape holds. */
  dropped: Array<{ scopeId: string; reason: string }>
}

export type SaveAgentLayerInput = {
  agentId: string
  /**
   * The version this edit was based on, sent as `If-Match`. Omitted when creating a layer
   * that does not exist yet — the server reads a present-but-wrong version as a conflict.
   */
  ifVersion?: number
  body: AgentBody
  displayName?: string
  description?: string
  enabled?: boolean
  changeNote?: string
}

const agentPath = (agentId: string): string => `/api/admin/agents/${encodeURIComponent(agentId)}`

export const fetchAgents = (): Promise<{ items: AgentSummary[] }> =>
  apiGet<{ items: AgentSummary[] }>('/api/admin/agents')

export const fetchAgent = (agentId: string): Promise<AgentDetail> => apiGet<AgentDetail>(agentPath(agentId))

export const fetchAgentVersions = (agentId: string): Promise<{ items: AgentVersion[] }> =>
  apiGet<{ items: AgentVersion[] }>(`${agentPath(agentId)}/versions`)

export const saveAgentLayer = ({ agentId, ifVersion, ...payload }: SaveAgentLayerInput): Promise<AgentLayer> =>
  api<AgentLayer>(agentPath(agentId), {
    method: 'PUT',
    headers: ifVersion === undefined ? undefined : { 'if-match': String(ifVersion) },
    body: JSON.stringify(payload)
  })

/** What the agent resolves to, without running it — the same for everyone who can use it. */
export const testResolveAgent = (agentId: string): Promise<AgentResolution> =>
  api<AgentResolution>(`${agentPath(agentId)}/test-resolve`, { method: 'POST', body: JSON.stringify({}) })

/**
 * Did this write lose an optimistic-concurrency race?
 *
 * Both statuses are treated as stale on purpose: the store raises a precondition failure
 * (412) for a version mismatch, and a route or a proxy in front of it may report the same
 * condition as 409. Either way the caller's copy is behind and the edit must not be
 * replayed blindly.
 */
export const isStaleWrite = (error: unknown): boolean =>
  error instanceof ApiRequestError && (error.status === 409 || error.status === 412)
